import { useState, useEffect, Fragment } from 'react'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { useLocation } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import AssessmentSideSelector from 'components/Assessment/AssessmentSideSelector'
import AssessmentSummaryExerciseSetListItem from 'components/WorkoutSummary/AssessmentSummaryExerciseSetListItem'

export default function CompletedAssessmentSummary() {

    const location = useLocation()

    const { exercises } = useGetExercises()
    const { assessmentWorkoutTemplate } = location.state || {};

    const [assessmentScore, setAssessmentScore] = useState(null)
    const [selectedSide, setSelectedSide] = useState('left')
    const [processedAssessmentWorkoutTemplate, setProcessedAssessmentWorkoutTemplate] = useState(null)

    const ProcessAssessmentWorkoutTemplate = () => {

        if (assessmentWorkoutTemplate === null) setProcessedAssessmentWorkoutTemplate(null)

        var totalReps = 0
        var totalRepsCompleted = 0

        var temp_processedAssessmentWorkoutTemplate = JSON.parse(JSON.stringify(assessmentWorkoutTemplate))

        temp_processedAssessmentWorkoutTemplate.phases.map((phase) => {

            phase.exercises.map((exercise) => {

                exercise.sets.map((set) => {

                    let user_score = 0;
                    let max_score = 1;

                    try {

                        if (exercise.lateral === 'bilateral') {

                            user_score = set.completedSetData.setUnitsCompleted;
                            max_score = set.reps;

                        }

                        if (exercise.lateral === 'unilateral') {
                            
                            user_score = set.completedSetData.setUnitsCompleted.leftSide + set.completedSetData.setUnitsCompleted.rightSide;
                            max_score = set.reps * 2;

                            // Per side processing to facilate the historical chart

                            var leftSidePercentageScore = (set.completedSetData.setUnitsCompleted.leftSide / set.reps) * 100
                            set.completedSetData.setUnitsCompleted.leftSidePercentageScore = leftSidePercentageScore
                            if (leftSidePercentageScore < 75) set.completedSetData.setUnitsCompleted.leftSideSummaryTag = 'weak'
                            if (75 <= leftSidePercentageScore) set.completedSetData.setUnitsCompleted.leftSideSummaryTag = 'strong'
                            
                            var rightSidePercentageScore = (set.completedSetData.setUnitsCompleted.rightSide / set.reps) * 100
                            set.completedSetData.setUnitsCompleted.rightSidePercentageScore = rightSidePercentageScore
                            if (rightSidePercentageScore < 75) set.completedSetData.setUnitsCompleted.rightSideSummaryTag = 'weak'
                            if (75 <= rightSidePercentageScore) set.completedSetData.setUnitsCompleted.rightSideSummaryTag = 'strong'

                        }

                    } catch (error) {

                        console.error('Error processing assessment workout template', error)

                    }
                    
                    // Calculate and store current percentage and tag for current set
                    var currentSetRepsCompletedPercentage = (user_score / max_score) * 100
                    set.completedSetData.percentageScore = currentSetRepsCompletedPercentage

                    if (currentSetRepsCompletedPercentage < 75) set.completedSetData.summaryTag = 'weak'
                    if (75 <= currentSetRepsCompletedPercentage) set.completedSetData.summaryTag = 'strong'

                    // Aggregate total reps completed
                    totalReps += max_score
                    if (set.completedSetData.completed) totalRepsCompleted += user_score

                    
                });
            });
        });

        // Sort exercises by percentageScore ascending
        temp_processedAssessmentWorkoutTemplate.phases.forEach(phase => {
            phase.exercises.sort((a, b) => {
                const aScore = a.sets[0].completedSetData.percentageScore
                const bScore = b.sets[0].completedSetData.percentageScore
                return aScore - bScore
            })
        })

        setProcessedAssessmentWorkoutTemplate(temp_processedAssessmentWorkoutTemplate)
        setAssessmentScore(Math.round((totalRepsCompleted / totalReps) * 100))

    }

    useEffect(() => {
        if (assessmentWorkoutTemplate !== null){ 
            ProcessAssessmentWorkoutTemplate()
        }
    }, [assessmentWorkoutTemplate])

    if (assessmentScore === null || exercises.length === 0){
        return (
            <ScreenFrame>
                <div className="max-w-xl mx-auto">
                    <div className='w-full h-[600px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    if (processedAssessmentWorkoutTemplate === null) {
        return (
            <ScreenFrame>
                <div className="max-w-xl mx-auto">
                    <ContentFrame>
                            <div className="p-4 text-center">Unable to find assessment workout template</div>
                    </ContentFrame>
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className="max-w-xl mx-auto space-y-4">
                <ContentFrame>
                    <div className='w-full pt-4 px-4'>
                        <div className='text-center text-gray-500 font-bold text-xs'>Total Movement Assessment Score</div>
                        
                        <div className='relative space-y-1'>
                            <div 
                                className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center font-bold flex items-center'
                                style={{ textShadow: '1px 1px 0 #333, -1px -1px 0 #333, 1px -1px 0 #333, -1px 1px 0 #333, 1px 1px 0 #333' }}
                            >
                                {assessmentScore < 50 && <ExclamationTriangleIcon className='mt-0.5 mr-2 h-4 w-4 self-center' />}
                                {assessmentScore}% ({assessmentScore < 75 ? 'WEAK' : 'STRONG'})
                            </div>

                            <div className="bg-gray-300 w-full h-8 rounded-lg overflow-hidden">
                                <div 
                                    className='h-8 bg-orange-400 rounded-r-lg'
                                    style={{ width: assessmentScore + '%' }} 
                                />
                            </div>
                        </div>
                    </div>

                    <div className="w-full p-4 overflow-hidden relative">        

                        {/* Left side y-axis */}
                        {/* <div className="absolute left-0 top-0 h-full flex flex-col justify-between items-center" style={{width: '24px'}}>
                            <div className="text-sm text-red-500 font-bold mt-8" style={{writingMode: 'vertical-rl', transform: 'rotate(180deg)'}}>
                                WEAKEST
                            </div>
                            <div className="text-sm text-green-500 font-bold mb-8" style={{writingMode: 'vertical-rl', transform: 'rotate(180deg)'}}>
                                STRONGEST
                            </div>
                        </div> */}

                        {/* Right side y-axis */}
                        {/* <div className="absolute right-0 top-0 h-full flex flex-col justify-between items-center" style={{width: '24px'}}>
                            <div className="text-sm text-red-500 font-bold mt-8" style={{writingMode: 'vertical-rl'}}>
                                WEAKEST
                            </div>
                            <div className="text-sm text-green-500 font-bold mb-8" style={{writingMode: 'vertical-rl'}}>
                                STRONGEST
                            </div>
                        </div> */}

                        <AssessmentSideSelector selectedSide={selectedSide} setSelectedSide={setSelectedSide} location='top' />

                        {processedAssessmentWorkoutTemplate.phases.map((phase, index) => (
                            <Fragment key={index}>
                                {phase.type === 'circuit' && (
                                    <>
                                        {Array.from({ length: phase.circuitRounds }).map((_, setIndex) => (
                                            <div 
                                                key={setIndex} 
                                                className={`${setIndex < phase.circuitRounds - 1 ? 'border-b border-gray-400' : ''}`}
                                            >
                                                {phase.exercises.map((exercise, exerciseIndex) => (
                                                    <div 
                                                        key={exerciseIndex} 
                                                        className={`${exerciseIndex < phase.exercises.length - 1 ? '' : ''}`}
                                                    >
                                                        <AssessmentSummaryExerciseSetListItem
                                                            selectedSide={selectedSide}
                                                            exerciseId={exercise.id}
                                                            exerciseName={exercise.name}
                                                            exerciseLateral={exercise.lateral}
                                                            repsAssigned={exercise.reps}
                                                            currentSet={exercise.sets[setIndex]}
                                                            percentageScore={exercise.sets[setIndex].completedSetData.percentageScore}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </>
                                )}
                            </Fragment>
                        ))}

                        <AssessmentSideSelector selectedSide={selectedSide} setSelectedSide={setSelectedSide} location='bottom' />
                    </div>
                </ContentFrame>
            </div>
        </ScreenFrame>
    )
}